import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services';
import { gtagService } from '../services/gtagService';
import { validatePhone, validateOTP, sanitizeInput, checkRateLimit } from '../utils/validation';

const Login: React.FC = () => {
  const { sendOTP, login, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [step, setStep] = useState<'phone' | 'register' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ phone?: string; otp?: string; name?: string; email?: string }>({});
  const [attempts, setAttempts] = useState({ phone: 0, otp: 0 });
  const [lastOtpSent, setLastOtpSent] = useState<number>(0);
  const [, setIsNewUser] = useState(false);
  const [, setOtpVerified] = useState(false);

  // Get the intended destination from location state
  const from = location.state?.from?.pathname || '/';

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting check
    if (!checkRateLimit(`phone_${phone}`, 3, 300000)) { // 3 attempts per 5 minutes
      setErrors({ phone: 'Too many attempts. Please try again in 5 minutes.' });
      return;
    }

    // Check cooldown period (30 seconds between OTP requests)
    const now = Date.now();
    if (lastOtpSent && now - lastOtpSent < 30000) {
      const remainingTime = Math.ceil((30000 - (now - lastOtpSent)) / 1000);
      setErrors({ phone: `Please wait ${remainingTime} seconds before requesting another OTP` });
      return;
    }

    const sanitizedPhone = sanitizeInput(phone);
    const phoneValidation = validatePhone(sanitizedPhone);
    
    if (!phoneValidation.isValid) {
      setErrors({ phone: phoneValidation.error });
      setAttempts(prev => ({ ...prev, phone: prev.phone + 1 }));
      return;
    }

    setErrors({});
    
    // Send OTP directly without checking if user exists
    setLastOtpSent(now);
    const result = await sendOTP(sanitizedPhone);
    
    if (result.success) {
      setStep('otp');
      setAttempts(prev => ({ ...prev, phone: 0 })); // Reset phone attempts on success
    } else {
      setErrors({ phone: result.error || 'Failed to send OTP. Please try again.' });
      setAttempts(prev => ({ ...prev, phone: prev.phone + 1 }));
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate name
    if (!name.trim() || name.trim().length < 2) {
      setErrors({ name: 'Please enter a valid name (at least 2 characters)' });
      return;
    }

    // Validate email if provided
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setErrors({});
    
    // Use the new completeRegistration endpoint to avoid duplicate OTP verification
    const result = await authService.completeRegistration(
      sanitizeInput(phone), 
      name.trim(), 
      email.trim() || undefined
    );
    
    if (result.success && result.data) {
      setAttempts({ phone: 0, otp: 0 });
      // Track signup event
      gtagService.trackSignup('phone');
      // The completeRegistration method already stores the auth data
      // We just need to reload the page or navigate
      window.location.href = from || '/';
    } else {
      setErrors({ name: result.error || 'Registration failed. Please try again.' });
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting for OTP verification
    if (!checkRateLimit(`otp_${phone}`, 5, 300000)) { // 5 attempts per 5 minutes
      setErrors({ otp: 'Too many failed attempts. Please request a new OTP.' });
      return;
    }

    const sanitizedOTP = sanitizeInput(otp);
    const otpValidation = validateOTP(sanitizedOTP);
    
    if (!otpValidation.isValid) {
      setErrors({ otp: otpValidation.error });
      setAttempts(prev => ({ ...prev, otp: prev.otp + 1 }));
      return;
    }

    setErrors({});
    
    // Try to login with OTP
    const result = await login(sanitizeInput(phone), sanitizedOTP);
    
    if (result.success) {
      setAttempts({ phone: 0, otp: 0 }); // Reset all attempts on success
      
      // Check if registration is required
      if ((result as any).requiresRegistration) {
        // New user - need registration after OTP verification
        setIsNewUser(true);
        setOtpVerified(true); // Mark OTP as verified
        setStep('register');
      } else {
        // Login successful
        gtagService.trackLogin('phone');
        navigate(from, { replace: true });
      }
    } else {
      // Invalid OTP or other error
      setAttempts(prev => ({ ...prev, otp: prev.otp + 1 }));
      
      if (attempts.otp >= 3) {
        setErrors({ otp: 'Multiple failed attempts. Please request a new OTP.' });
        setStep('phone');
        setOtp('');
      } else {
        setErrors({ otp: result.error || 'Invalid OTP. Please try again.' });
      }
    }
  };

  const handleResendOTP = async () => {
    // Check cooldown period for resend
    const now = Date.now();
    if (lastOtpSent && now - lastOtpSent < 30000) {
      const remainingTime = Math.ceil((30000 - (now - lastOtpSent)) / 1000);
      setErrors({ otp: `Please wait ${remainingTime} seconds before requesting another OTP` });
      return;
    }

    // Rate limiting for resend
    if (!checkRateLimit(`resend_${phone}`, 3, 600000)) { // 3 resends per 10 minutes
      setErrors({ otp: 'Too many resend attempts. Please try again later.' });
      return;
    }

    setLastOtpSent(now);
    const result = await sendOTP(sanitizeInput(phone));
    if (result.success) {
      setOtp('');
      setErrors({});
      setAttempts(prev => ({ ...prev, otp: 0 })); // Reset OTP attempts on resend
    } else {
      setErrors({ otp: result.error || 'Failed to resend OTP. Please try again.' });
    }
  };

  const handleEditPhone = () => {
    setStep('phone');
    setOtp('');
    setName('');
    setEmail('');
    setErrors({});
    clearError(); // Clear AuthContext error
    setAttempts({ phone: 0, otp: 0 });
    setIsNewUser(false);
  };

  return (
    <div className="container py-8 sm:py-12 lg:py-16 max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-2">
            {step === 'register' ? 'Create Account' : 'Welcome Back!'}
          </h1>
          <p className="text-gray-600">
            {step === 'phone' 
              ? 'Enter your mobile number to continue' 
              : step === 'register'
              ? 'Your OTP is verified! Please provide your details to complete registration'
              : 'Enter the OTP sent to your mobile number'
            }
          </p>
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
              <button
                onClick={clearError}
                className="text-red-500 hover:text-red-700 text-xs mt-1 underline"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>

        {step === 'phone' ? (
          <form onSubmit={handleSendOTP} className="space-y-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  +91
                </span>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="98765 43210"
                  maxLength={10}
                />
              </div>
              {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading || phone.length !== 10}
              className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                isLoading || phone.length !== 10
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-accent-600 text-white hover:bg-accent-700 hover:shadow-lg hover:shadow-accent-500/25 hover:scale-[1.02]'
              }`}
            >
              {isLoading ? 'Processing...' : 'Continue'}
            </button>
          </form>
        ) : step === 'register' ? (
          <form onSubmit={handleRegister} className="space-y-6">
            <div className="mb-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-green-700 text-sm font-medium">✓ OTP Verified Successfully</p>
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                  <span>Mobile: +91 {phone}</span>
                  <button
                    type="button"
                    onClick={handleEditPhone}
                    className="text-accent-500 hover:text-accent-600 font-medium"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your full name"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your email address"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading || !name.trim()}
              className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                isLoading || !name.trim()
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-accent-600 text-white hover:bg-accent-700 hover:shadow-lg hover:shadow-accent-500/25 hover:scale-[1.02]'
              }`}
            >
              {isLoading ? 'Creating Account...' : 'Create Account & Continue'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                Enter OTP
              </label>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-gray-600">Sent to +91 {phone}</span>
                <button
                  type="button"
                  onClick={handleEditPhone}
                  className="text-accent-500 hover:text-accent-600 text-sm font-medium"
                >
                  Edit
                </button>
              </div>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={`w-full px-4 py-3 border rounded-lg text-center text-2xl tracking-widest focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                  errors.otp ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="000000"
                maxLength={6}
              />
              {errors.otp && <p className="text-red-500 text-sm mt-1">{errors.otp}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading || otp.length !== 6}
              className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                isLoading || otp.length !== 6
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-accent-600 text-white hover:bg-accent-700 hover:shadow-lg hover:shadow-accent-500/25 hover:scale-[1.02]'
              }`}
            >
              {isLoading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">Didn't receive the OTP?</p>
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isLoading}
                className="text-accent-600 hover:text-accent-700 font-medium text-sm transition-all duration-300 hover:scale-105"
              >
                Resend OTP
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-neutral-600 hover:text-accent-600 transition-all duration-300 text-sm hover:scale-105"
          >
            ← Back to Shopping
          </Link>
        </div>
      </div>

      <div className="mt-6 text-center text-xs text-gray-500">
        <p>Enter the 6-digit OTP sent to your mobile number</p>
      </div>
    </div>
  );
};

export default Login;